import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

interface Tester {
  email: string;
  added_by: string | null;
  created_at: string | null;
}

/**
 * Manages the sajug fortune-engine A/B allowlist. Only emails listed here may switch to engine 'B'
 * in the app's /settings toggle. Talks to the sajug backend via the /api/admin/internal-testers
 * rewrite (see vercel.json / vite.config.ts).
 */
export default function InternalTestersPage() {
  const { token: accessToken } = useAuth();
  const [testers, setTesters] = useState<Tester[]>([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const xsrfCookie = document.cookie.split('; ').find((row) => row.startsWith('XSRF-TOKEN='));
    if (xsrfCookie) headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
    return headers;
  }, [accessToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<{ testers: Tester[] }>('/api/admin/internal-testers', {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setTesters(res.data.testers || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || '목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const value = email.trim();
    if (!value) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        '/api/admin/internal-testers',
        { email: value },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setEmail('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || '추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (target: string) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`/api/admin/internal-testers/${encodeURIComponent(target)}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || '삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl p-6">
      <Card>
        <CardHeader>
          <CardTitle>내부 테스터 · 다온 사주 무료 이용 &amp; 운세 엔진 A/B</CardTitle>
          <p className="text-sm text-muted-foreground">
            여기 등록된 내부자 이메일만 다온 사주(saju.ugot.uk)를 구슬(닢) 결제 없이 무료로 이용할 수 있으며, 사주 앱 설정에서 운세 엔진 B(실험)로 전환할 수 있습니다.
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex gap-2">
            <Input
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
            <Button onClick={add} disabled={loading || !email.trim()}>
              추가
            </Button>
          </div>
          {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>이메일</TableHead>
                <TableHead>추가자</TableHead>
                <TableHead>추가일</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {testers.map((t) => (
                <TableRow key={t.email}>
                  <TableCell className="font-mono">{t.email}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.added_by || '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {t.created_at ? t.created_at.slice(0, 10) : '-'}
                  </TableCell>
                  <TableCell>
                    <Button variant="destructive" size="sm" onClick={() => remove(t.email)}>
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {testers.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} className="py-6 text-center text-muted-foreground">
                    등록된 내부 테스터가 없습니다.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
